import React, { useState } from "react";
import axios from "axios";
import styles from "./OrderList.module.scss";

import moment from "moment";
import { DeliveryProps } from "../../types/orders";

type Props = {
  deliveries: DeliveryProps[];
  pageNumber: number;
  pageSize: number;
  isLoading: boolean;
  onDeleteDelivery?: (id: number) => Promise<void>;
};

const DeliveryList = ({
  deliveries,
  pageNumber,
  pageSize,
  isLoading,
  onDeleteDelivery,
}: Props) => {
  const [loadingPdfId, setLoadingPdfId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const getAmount = (delivery: DeliveryProps) => {
    return delivery.payment ? delivery.payment.amount : 0;
  };

  const handleViewPdf = async (transactionId: number) => {
    setLoadingPdfId(transactionId);

    try {
      const token = localStorage.getItem("authToken");
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || "";

      const response = await axios.get(
        `${apiBaseUrl}/v1/admin/receive-transactions/${transactionId}/pdf`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            apikey: process.env.REACT_APP_API_KEY,
          },
          responseType: "blob",
        }
      );

      const blob = new Blob([response.data], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, "_blank");

      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    } catch (error) {
      console.error("Error fetching PDF:", error);
      alert("PDF લોડ કરવામાં ભૂલ થઈ. કૃપા કરીને ફરીથી પ્રયત્ન કરો.");
    }

    setLoadingPdfId(null);
  };

  const handleDelete = async (id: number) => {
    if (!onDeleteDelivery) return;

    if (!window.confirm("શું તમે ખરેખર આ રસીદ ડિલીટ કરવા માંગો છો?")) {
      return;
    }

    setDeletingId(id);
    try {
      await onDeleteDelivery(id);
    } finally {
      setDeletingId(null);
    }
  };

  const canDelete = (delivery: DeliveryProps) => {
    return !delivery.payment && delivery.order?.status === 0;
  };

  return (
    <div className={styles.OrderList}>
      <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
          <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider"
                  >
                    Sr. No.
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider"
                  >
                    રસીદ નં.
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider"
                  >
                    ઓર્ડર ID
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider"
                  >
                    પાર્ટી
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider"
                  >
                    આઈટમ
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider"
                  >
                    ડી. નંગ
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider"
                  >
                    ડી. વજન
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider"
                  >
                    ટોટલ રૂ
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider"
                  >
                    ડિલિવરી તારીખ
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-right text-base text-primary-1 font-bold uppercase tracking-wider"
                  >
                    એકશન્સ બટન
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={10}
                      className="text-primary-2 font-bold text-xl text-center py-2"
                    >
                      રાહ જુઓ...
                    </td>
                  </tr>
                ) : (
                  <>
                    {deliveries.length > 0 ? (
                      <>
                        {deliveries.map((delivery, index) => (
                          <tr key={index}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {(pageNumber - 1) * pageSize + index + 1}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {delivery.id || "-"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {delivery.order?.id || "-"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {delivery.order?.party?.firm_name || "-"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {delivery.order?.item?.name || "-"}
                              {delivery.order?.item?.isinset ? (
                                <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
                                  સેટ
                                </span>
                              ) : <></>}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {delivery.order?.item?.isinset ? (
                                <div className="space-y-1">
                                  {(delivery.qty_field_2 || 0) > 0 && (
                                    <div>
                                      <span className="text-gray-500">કેશ: </span>
                                      <span>{delivery.qty_field_2 || 0}</span>
                                    </div>
                                  )}
                                  {(delivery.qty_field_1 || 0) > 0 && (
                                    <div>
                                      <span className="text-gray-500">પટ્ટા: </span>
                                      <span>{delivery.qty_field_1 || 0}</span>
                                    </div>
                                  )}
                                  {(delivery.qty_field_1 || 0) === 0 && (delivery.qty_field_2 || 0) === 0 && "-"}
                                </div>
                              ) : (
                                <span>{delivery.qty_field_1 || 0}</span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {delivery.order?.item?.isinset ? (
                                <div className="space-y-1">
                                  {(delivery.qty_field_2 || 0) > 0 && (
                                    <div>
                                      <span className="text-gray-500">કેશ: </span>
                                      <span>{delivery.weight_field_2 ? delivery.weight_field_2.toFixed(3) : "0.000"}</span>
                                    </div>
                                  )}
                                  {(delivery.qty_field_1 || 0) > 0 && (
                                    <div>
                                      <span className="text-gray-500">પટ્ટા: </span>
                                      <span>{delivery.weight_field_1 ? delivery.weight_field_1.toFixed(3) : "0.000"}</span>
                                    </div>
                                  )}
                                  {(delivery.qty_field_1 || 0) === 0 && (delivery.qty_field_2 || 0) === 0 && "-"}
                                </div>
                              ) : (
                                <span>{delivery.weight_field_1 ? delivery.weight_field_1.toFixed(3) : "0.000"}</span>
                              )}
                            </td>
                            <td className="text-right px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {getAmount(delivery).toFixed(2) || "-"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {delivery?.created_at
                                ? moment(delivery.created_at).format("DD-MM-YYYY hh:mm A")
                                : "-"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              {deletingId === delivery.id ? (
                                <p className="text-primary-2 font-bold text-right text-lg">
                                  રાહ જુઓ...
                                </p>
                              ) : (
                                <>
                                  <button
                                    onClick={() => handleViewPdf(delivery.id)}
                                    disabled={loadingPdfId === delivery.id}
                                    className="text-indigo-600 hover:text-indigo-900 disabled:opacity-50"
                                  >
                                    {loadingPdfId === delivery.id ? "લોડ..." : "PDF"}
                                  </button>
                                  {canDelete(delivery) && (
                                    <button
                                      onClick={() => handleDelete(delivery.id)}
                                      disabled={deletingId === delivery.id}
                                      className="text-red-600 hover:text-red-900 ml-4"
                                    >
                                      Delete
                                    </button>
                                  )}
                                </>
                              )}
                            </td>
                          </tr>
                        ))}
                      </>
                    ) : (
                      <tr>
                        <td
                          colSpan={10}
                          className="text-primary-2 font-bold text-xl text-center py-2"
                        >
                          ડિલિવરી ની વિગતો મળી નથી
                        </td>
                      </tr>
                    )}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};
export default DeliveryList;
